/**
 * Utility to convert capability IDs to IntegrationType
 * 
 * Maps capability IDs (which may have suffixes like "_control", "_readonly")
 * to IntegrationType values for the integration system.
 */

import type { IntegrationType } from "@/types/integration";

/**
 * Convert a capability ID to IntegrationType
 * Returns a valid IntegrationType or null if no mapping exists
 */
export function capabilityIdToIntegrationType(capabilityId: string): IntegrationType | null {
  // Remove common suffixes
  const normalizedId = capabilityId
    .replace(/_control$/, "")
    .replace(/_readonly$/, "")
    .replace(/_read$/, "")
    .replace(/_sync$/, "")
    .replace(/_summary$/, "")
    .replace(/_bot$/, "")
    .replace(/_api$/, "")
    .replace(/_reviews$/, "")
    .replace(/_reporting$/, "")
    .replace(/_intelligence$/, "")
    .replace(/_billing$/, "")
    .replace(/_posting$/, "")
    .replace(/_tracking$/, "")
    .replace(/_automation$/, "")
    .replace(/_ads$/, "_ads")
    .replace(/_pack$/, "");

  // Comprehensive type mapping
  const typeMap: Record<string, IntegrationType> = {
    // Media & Entertainment
    spotify: "spotify",
    apple_music: "apple_music",
    soundcloud: "soundcloud",
    twitch: "twitch",
    vimeo: "vimeo",
    youtube_upload: "youtube_upload",
    // Productivity
    gmail: "gmail",
    google_calendar: "google_calendar",
    google_drive: "google_drive",
    google_docs: "google_docs",
    google_sheets: "google_sheets",
    google_workspace: "google_workspace",
    microsoft_365: "microsoft_365",
    outlook: "outlook",
    onedrive: "onedrive",
    sharepoint: "sharepoint",
    notion: "notion",
    airtable: "airtable",
    trello: "trello",
    asana: "asana",
    jira: "jira",
    confluence: "confluence",
    monday: "monday",
    clickup: "clickup",
    // Messaging
    telegram: "telegram",
    discord: "discord",
    slack: "slack",
    microsoft_teams: "microsoft_teams",
    whatsapp: "whatsapp",
    twilio_sms: "twilio_sms",
    twilio_mms: "twilio_mms",
    twilio_voice: "twilio_voice",
    sendgrid: "sendgrid",
    mailgun: "mailgun",
    postmark: "postmark",
    ses: "ses",
    // Payments
    stripe: "stripe",
    stripe_billing: "stripe_billing",
    paypal: "paypal",
    adyen: "adyen",
    braintree: "braintree",
    plaid: "plaid",
    // E-Commerce
    shopify: "shopify",
    woocommerce: "woocommerce",
    bigcommerce: "bigcommerce",
    square: "square",
    etsy: "etsy",
    amazon: "amazon",
    ebay: "ebay",
    magento: "magento",
    prestashop: "prestashop",
    opencart: "opencart",
    squarespace: "squarespace",
    wix: "wix",
    // Social Media
    twitter: "twitter",
    instagram: "instagram",
    facebook: "facebook",
    meta_graph: "meta_graph",
    youtube: "youtube",
    tiktok: "tiktok",
    tiktok_ads: "tiktok_ads",
    linkedin: "linkedin",
    reddit: "reddit",
    pinterest: "pinterest",
    snapchat_ads: "snapchat_ads",
    google_ads: "google_ads",
    meta_ads: "meta_ads",
    // CRM
    hubspot: "hubspot",
    salesforce: "salesforce",
    zoho: "zoho",
    pipedrive: "pipedrive",
    zendesk: "zendesk",
    intercom: "intercom",
    freshdesk: "freshdesk",
    servicenow: "servicenow",
    // Accounting
    xero: "xero",
    quickbooks: "quickbooks",
    myob: "myob",
    // Identity & Auth
    oauth_google: "oauth_google",
    oauth_apple: "oauth_apple",
    oauth_microsoft: "oauth_microsoft",
    oauth_meta: "oauth_meta",
    sso_okta: "sso_okta",
    sso_azure_ad: "sso_azure_ad",
    sso_ping: "sso_ping",
    passkeys: "passkeys",
    webauthn: "webauthn",
    mfa_twilio: "mfa_twilio",
    mfa_authy: "mfa_authy",
    mfa_totp: "mfa_totp",
    iam_auth0: "iam_auth0",
    iam_okta: "iam_okta",
    iam_aws: "iam_aws",
    // Automation
    zapier: "zapier",
    make: "make",
    n8n: "n8n",
    webhook: "webhook",
    // Crypto
    binance: "binance",
    coinbase: "coinbase",
    kraken: "kraken",
    okx: "okx",
    bybit: "bybit",
  };

  // Try normalized ID first
  if (typeMap[normalizedId]) return typeMap[normalizedId];

  // Try original ID
  if (typeMap[capabilityId]) return typeMap[capabilityId];

  // Try base ID (first part before underscore)
  const baseId = capabilityId.split("_")[0];
  if (typeMap[baseId]) return typeMap[baseId];

  // No mapping found
  return null;
}

/**
 * Get IntegrationType for a capability, with fallback
 * Returns a valid IntegrationType or "webhook" as safe fallback
 */
export function getIntegrationTypeForCapability(capabilityId: string): IntegrationType {
  return capabilityIdToIntegrationType(capabilityId) || "webhook";
}


